import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AutoComplete, Avatar, Button, Collapse, Form, Input, List, Progress, Tag } from 'antd';
import RoundMoreButton from '../../../../microcomponents/RoundMoreButton';

import { HugeiconsIcon, IconSvgElement } from '@hugeicons/react';
import {
  GiftIcon,
  MailSend01Icon,
  NotificationOff01Icon, PencilEdit01FreeIcons,
  PlusSignIcon,
  RefreshIcon,
  StarIcon, UserIcon
} from '@hugeicons/core-free-icons';
import Wrapper from '../../../../components/Wrapper';
import {
  IconArrowLeft,
  IconEdit, IconGif, IconGift,
  IconPencil,
  IconPlus, IconRefresh, IconStar,
  IconTrash, IconUser, IconUsersGroup
} from '@tabler/icons-react';
import CModal from '../../../../components/CModal';
import BackButton from '../../../../microcomponents/BackButton';
import CustomerMetadataModal from '../../../../components/customers/CustomerMetadataModal';
import { ReactNode, useState } from 'react';
import { useMutation, useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';
import { customerQueryOptions, profileQueryOptions } from '../../../../utils/queryOptions';
import dayjs from 'dayjs';
import { COLORS } from '@next-is-free-monorepo/globals';
import { apiV1 } from '../../../../utils/apiClient';
import RedeemPointsModal from '../../../../components/customers/RedeemPointsModal';
import AddPointsModal from '../../../../components/customers/AddPointsModal';
import {
  CustomerHistoryRecord,
  CustomerPaginatedHistory,
  PaginatedTransactions
} from '@next-is-free-monorepo/next-is-free-types';
import SingleCollapse from '../../../../components/SingleCollapse';
import * as React from 'react';
import PageLoader from '../../../../microcomponents/PageLoader';
import NAvatar from '../../../../../../libs/nif-web-components/src/microcomponents/NAvatar';
import { BgIconProps, NActionIcon, NBgIcon, NButton } from '@next-is-free-monorepo/nif-web-components';

export const Route = createFileRoute('/_authenticated/customers/$customerId')({
  component: RouteComponent,
  pendingComponent: props => <PageLoader />
});

function RouteComponent() {
  const { customerId } = Route.useParams();

  const navigate = useNavigate();

  const [metadataModalOpen, setMetadataModalOpen] = useState(false);
  const [selectedGoalIndex, setSelectedGoalIndex] = useState<undefined | number>(undefined);
  const [addPointsModalOpen, setAddPointsModalOpen] = useState(false);

  const customer = useSuspenseQuery(customerQueryOptions(customerId));

  const fallbackDisplayName = customer.data.display_name ?? '@' + customer.data.username;

  const shopProfile = useSuspenseQuery(profileQueryOptions());

  const history = useSuspenseInfiniteQuery({
    queryKey: ['customer-history', customerId],
    queryFn: async ({ pageParam }) => await apiV1.get<CustomerPaginatedHistory>(`/shop/customers/${customerId}/history?page=${pageParam}`),
    getNextPageParam: (lastPage, allPages) => lastPage.hasMore ? allPages.length + 1 : undefined,
    initialPageParam: 1
  });

  const highestGoal = shopProfile.data.goals[shopProfile.data.goals.length - 1];

  return (
    <div>
      <div className={'flex items-center gap-2 justify-between border-b px-4 h-[50px]'}>
        <div className={'flex gap-2 items-center'}>
          <BackButton hideText fallBackRouteOptions={{ to: '/customers' }} />
          <NAvatar size={'small'} text={customer.data.username} />
          <h1 className={'font-medium text-xl'}>{customer.data.display_name ?? '@' + customer.data.username}</h1>
        </div>
        <div className={'gap-2 hidden sm:flex'}>
          <NButton color={'outline'} size={'small'}
                   hugeIcon={!customer.data.notifications_allowed ? NotificationOff01Icon : MailSend01Icon}
                   onClick={() => navigate({
                     to: '/notifications/new',
                     search: {
                       recipients: {
                         type: 'SINGLE_USERS',
                         recipients: [{ label: fallbackDisplayName, id: customer.data.id }]
                       }
                     }
                   })} disabled={!customer.data.notifications_allowed}>
            Invia Notifica
          </NButton>
          <NButton size={'small'} color={'secondary'} onClick={() => setAddPointsModalOpen(true)}>
            Assegna punti
          </NButton>
        </div>
      </div>
      <div className={'flex'}>
        <div className={'flex-1 p-4 overflow-scroll scrollbar-track-white scrollbar-thin scrollbar-thumb-slate-200'}
             style={{ height: 'calc(100vh - 50px)' }}>
          <h2 className={'font-medium text-lg mb-5'}>Storico</h2>
          <div className={'flex flex-col'}>
            {history.data?.pages.map((page) => page.records).flat().map((item) => (
              <div key={item.id}>
                <HistoryRecord record={item} />
              </div>
            ))}
            {history.hasNextPage ?
              <Button className={'m-auto'} loading={history.isFetchingNextPage} onClick={() => history.fetchNextPage()}>Carica
                altri...</Button> :
              <div className={'flex mb-1 gap-2'}>
                <div className={'flex flex-col items-center justify-between gap-2 mt-2'}>
                  <NAvatar hugeIcon={UserIcon} color={'outline'}/>
                </div>
                <div className={`w-full p-2 rounded`}>
                  <div className={'flex gap-2'}>
                    <div>
                      <p
                        className={'text-slate-400 text-sm'}>{dayjs(customer.data.added_at).format('DD/MM/YYYY HH:mm')}</p>
                      <p className={'font-medium'}>Il cliente ha aggiunto il tuo negozio</p>
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>
        </div>
        <div className={'w-[50%] max-w-md border-l overflow-scroll hidden pb-[50px] sm:block'}
             style={{ height: 'calc(100vh - 50px)' }}>
          <div className={'p-4'}>
            <p className={'text-lg font-semibold'}>
              Dettagli Cliente
            </p>
            <div className={'py-4'}>
              <div className={'flex justify-between gap-4'}>
                <p className={'font-medium'}>Username</p>
                <p>@{customer.data.username}</p>
              </div>
              <div className={'flex justify-between gap-4'}>
                <p className={'font-medium'}>Nome completo</p>
                <p>{customer.data.display_name ?? '-'}</p>
              </div>
              <div className={'flex justify-between gap-4'}>
                <p className={'font-medium'}>Compleanno</p>
                <p>{customer.data.birthdate ? dayjs(customer.data.birthdate).format('DD/MM') : '-'}</p>
              </div>
              <div className={'flex justify-between gap-4 mt-4'}>
                <p className={'font-medium'}>Aggiunto il</p>
                <p>{dayjs(customer.data.added_at).format('DD/MM/YYYY HH:mm')}</p>
              </div>
              {/*    <div className={'flex justify-between gap-4'}>
              <p className={'font-medium'}>Spesa totale</p>
              <p>20€</p>
            </div>*/}
              <div className={'flex justify-between gap-4'}>
                <p className={'font-medium'}>Punti attuali</p>
                <p>{customer.data.points}</p>
              </div>
              <div className={'flex justify-between gap-4'}>
                <p className={'font-medium'}>Notifiche attive</p>
                <p>{customer.data.notifications_allowed ? 'Si' : 'No'}</p>
              </div>
            </div>
            <div className={'my-3 border-t pt-4'}>
              <p className={'font-medium mb-4'}>Programma fedeltà</p>
              <div className={'flex justify-end font-medium mb-2'} style={{ color: shopProfile.data.brand_color }}>
                <p>{customer.data.points}/{highestGoal.number}</p>
              </div>
              <Progress percent={customer.data.points / highestGoal.number * 100} className={'w-full'} strokeWidth={25}
                        showInfo={false} trailColor={COLORS.slate[200]} strokeColor={shopProfile.data.brand_color} />
              <div className={'grid grid-cols-3 gap-2 mt-2'}>
                {shopProfile.data.goals.map((goal, index) => (
                  <div onClick={() => {
                    customer.data.points >= goal.number && setSelectedGoalIndex(index);
                  }} key={index}
                       style={{
                         borderColor: customer.data.points >= goal.number ? shopProfile.data.brand_color : '',
                         color: customer.data.points >= goal.number ? shopProfile.data.brand_color : ''
                       }}
                       className={`border-slate-300 rounded-xl bg-slate-50 text-slate-400 p-2 border aspect-square flex flex-col justify-end ${customer.data.points >= goal.number ? 'bg-white cursor-pointer hover:opacity-85' : ''}`}>
                    <p>{goal.number}</p>
                    <p className={'font-bold line-clamp-2 '}>{goal.title}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className={'py-4 border-t mt-8'}>
              <div className={'flex items-center justify-between mb-2'}>
                <p className={'font-medium text-slate-500'}>
                  Campi personalizzati
                </p>
                <NActionIcon
                  hugeIcon={PencilEdit01FreeIcons}
                  onClick={() => setMetadataModalOpen(true)}
                  size={'small'} color={'tertiary'}
                >
                </NActionIcon>
              </div>
              {Object.entries(customer.data.metadata).map(([key, value]) => (
                <div key={key} className="flex justify-between gap-4">
                  <p className="font-medium">{key}</p>
                  <p>{value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <CustomerMetadataModal
          customer={customer.data}
          open={metadataModalOpen}
          onClose={() => setMetadataModalOpen(false)}
        />
        <RedeemPointsModal onClose={() => setSelectedGoalIndex(undefined)} open={selectedGoalIndex !== undefined}
                           customerId={customerId} goalIndex={selectedGoalIndex} />
        <AddPointsModal open={addPointsModalOpen} onClose={() => setAddPointsModalOpen(false)}
                        customerId={customerId} />
      </div>
    </div>
  );
}


//history record
function HistoryRecord({ record }: { record: CustomerHistoryRecord }) {

  const [open, setOpen] = useState(false);

  type TransactionIcon = {
    icon: IconSvgElement;
    color: BgIconProps['color'];
  };

  type Icons = {
    transaction: {
      [key: number]: TransactionIcon;
    };
  };

  const icons: Icons = {
    transaction: {
      0: {
        icon: PlusSignIcon,
        color: 'success'
      },
      1: {
        icon: StarIcon,
        color: 'info'
      },
      2: {
        icon: GiftIcon,
        color: 'info'
      },
      3: {
        icon: RefreshIcon,
        color: 'secondary'
      }
    }
  };


  function Header() {

    const { cancelled } = record;

    if (record.record_type === 'transaction') {
      switch (record.type) {
        case 0:
          return (<p className={`font-medium ${cancelled && 'line-through'}`}>{record.points + ' Punti assegnati'}</p>);
        case 1:
          return (<p className={`font-medium ${cancelled && 'line-through'}`}>{record.goal_name}</p>);
        case 2:
          return (<p className={`font-medium ${cancelled && 'line-through'}`}>{record.goal_name}</p>);
        case 3:
          return (<p className={`font-medium ${cancelled && 'line-through'}`}>Punti ripristinati</p>);
      }
    }
  }

  function Details() {
    const { cancelled } = record;

    if (record.record_type === 'transaction') {
      switch (record.type) {
        case 0:
          return (
            <div className={'flex flex-col gap-2 font-medium'}>
              <div>
                <p className={'text-slate-500'}>Spesa</p>
                <p>{record.euro}€</p>
              </div>
              <div>
                <p className={'text-slate-500'}>Punto vendita</p>
                <p>{record.sales_point_name}</p>
              </div>
              <div>
                <p className={'text-slate-500'}>Membro dello Staff</p>
                <p>{record.staff_member_display_name ?? 'Amministratore'}</p>
              </div>
              {record.cancelled &&
                <div>
                  <p className={'text-slate-500'}>Annullato il:</p>
                  <p>{dayjs(record.cancelled_at).format('DD/MM/YYYY HH:mm')}</p>
                </div>
              }
            </div>
          );
        case 1:
          return (<div className={'flex flex-col font-medium gap-2'}>
            <div>
              <p className={'text-slate-500'}>Punti spesi</p>
              <p>{record.points}</p>
            </div>
            <div>
              <p className={'font-medium text-slate-500'}>Punto vendita</p>
              <p>{record.sales_point_name}</p>
            </div>
            <div>
              <p className={'font-medium text-slate-500'}>Membro dello Staff</p>
              <p>{record.staff_member_display_name ?? 'Amministratore'}</p>
            </div>
            {record.cancelled &&
              <div>
                <p className={'font-medium text-slate-500'}>Annullato il:</p>
                <p>{dayjs(record.cancelled_at).format('DD/MM/YYYY HH:mm')}</p>
              </div>
            }
          </div>);
        case 2:
          return (<p className={`font-medium ${cancelled && 'line-through'}`}>{record.goal_name}</p>);
        case 3:
          return (<p>Punti ripristinati per inutilizzo</p>);
      }
    }
  }

  return (
    <div className={'flex mb-1 gap-2'}>
      <div className={'flex flex-col items-center justify-between gap-2 mt-2'}>
        <NBgIcon color={icons[record.record_type][record.type].color} className={'rounded-full'}
                 hugeIcon={icons[record.record_type][record.type].icon} />
        <div className={`w-[2px] h-full rounded bg-slate-100`}></div>
      </div>
      <div className={`w-full p-2 rounded hover:bg-slate-50 ${open && 'bg-slate-50'}`}>
        <SingleCollapse onChange={setOpen} header={
          <div className={'flex gap-2'}>
            <div>
              <p className={'text-slate-400 text-sm'}>{dayjs(record.created_at).format('DD/MM/YYYY HH:mm')}</p>
              <Header />
            </div>
          </div>
        }>
          <div className={'border bg-white p-2 rounded'}>
            <Details />
          </div>
        </SingleCollapse>
      </div>
    </div>
  );
}
